import { useMemo, useState } from 'react';
import { Heading, TableState } from '@nbirl/nbi-common-ui-libs';

import {
  DocumentsListTable,
  ShareDocumentModal,
} from '../../documents/components';
import { DocumentListRow, DocumentsListState } from '../../documents/models';
import {
  getDocumentListColumns,
  orderBySortString,
} from '../../documents/utils';
import { useFolderTree } from '../../folders/context';
import { Resource } from '../models';

type ResourceListProps = {
  heading: React.ReactNode;
  query: DocumentsListState;
  totalElements: number;
  pageCount: number;
  emptyState: React.ReactNode;
  isLoading: boolean;
  list?: Resource;
  error?: unknown;
  disableStatusSort?: boolean;
  onRefetch: () => void;
  onQueryChange: (query: DocumentsListState) => void;
};

export const ResourceList = ({
  heading,
  query,
  totalElements,
  pageCount,
  emptyState,
  list,
  isLoading,
  error,
  disableStatusSort,
  onRefetch,
  onQueryChange,
}: ResourceListProps) => {
  const [updatingStatuses, setUpdatingStatuses] = useState<string[]>([]);
  const [shareDocumentIds, setShareDocumentIds] = useState<{
    documentId: string;
    folderId: string;
  } | null>(null);
  const [rowIds, setRowIds] = useState<Record<string, boolean>>({});
  const { refetch, handleSelectFolder } = useFolderTree();

  const { search, ...tableState } = query;
  const isSearching = Boolean(search);

  const sortedData: Omit<DocumentListRow, 'parentFolderId'>[] = useMemo(
    () => [
      ...orderBySortString(list?.folders || [], tableState.sortBy).map(
        (folder) => ({
          ...folder,
          isFolder: true,
        })
      ),
      ...orderBySortString(list?.documents || [], tableState.sortBy).map(
        (document) => ({
          ...document,
          isFolder: false,
        })
      ),
    ],
    [list, tableState.sortBy]
  );

  const columns = useMemo(
    () =>
      getDocumentListColumns({
        isSearching,
        updatingStatuses,
        subFolders: list?.folders || [],
        disableStatusSort,
        onDocumentStatusUpdateStart: (id) =>
          setUpdatingStatuses((statuses) => [...statuses, id]),
        onDocumentStatusUpdateFinish: (id) =>
          setUpdatingStatuses((statuses) =>
            statuses.filter((status) => status !== id)
          ),
        onFolderChange: handleSelectFolder,
        onFolderTreeChange: refetch,
        onRefetchList: onRefetch,
        onShareDocument: (id) =>
          setShareDocumentIds({
            documentId: id,
            folderId: id,
          }),
      }),
    [
      isSearching,
      updatingStatuses,
      list?.folders,
      disableStatusSort,
      handleSelectFolder,
      refetch,
      onRefetch,
    ]
  );

  const handleStateChange = ({ selectedRowIds, ...state }: TableState) => {
    onQueryChange({ ...query, ...state });
    setRowIds(selectedRowIds);
  };

  return (
    <>
      <Heading className="py-6 mb-1">{heading}</Heading>

      <DocumentsListTable
        columns={columns}
        data={sortedData as DocumentListRow[]}
        isLoading={isLoading}
        error={error}
        isSearching={isSearching}
        totalCount={totalElements}
        pageCount={pageCount}
        tableState={tableState}
        selectedRowIds={rowIds}
        emptyState={emptyState}
        onStateChange={handleStateChange}
        onDeselectRows={() => setRowIds({})}
        onBulkStatusChange={onRefetch}
      />

      {shareDocumentIds && (
        <ShareDocumentModal
          isPending={
            sortedData.find((row) => row.id === shareDocumentIds.documentId)
              ?.status === 'PENDING'
          }
          folderId={shareDocumentIds.folderId}
          documentId={shareDocumentIds.documentId}
          onDismiss={() => setShareDocumentIds(null)}
        />
      )}
    </>
  );
};
